import { DoNotDisturbAlt, OpenInNew } from "@mui/icons-material";
import { Card, CircularProgress, Grid, Stack, Typography } from "@mui/material";
import { type Currency, UserLocale } from "@prisma/client";
import classNames from "classnames";
import React, { type ComponentProps, useRef } from "react";
import { EmployeePicture } from "~/components/employee/EmployeePicture";
import { type Input as MarketDataInput } from "~/components/market-data/MarketDataBrowser";
import { MarketDataChartBackground } from "~/components/market-data/MarketDataChartBackground";
import { MarketDataChartTooltip } from "~/components/market-data/MarketDataChartTooltip";
import { HtmlTooltip } from "~/components/ui/core/HtmlTooltip";
import { Link } from "~/components/ui/core/Link";
import { Ping } from "~/components/ui/core/Ping";
import { DataQualityIndicator } from "~/components/ui/DataQualityIndicator";
import { type LocationForSelector } from "~/components/ui/LocationSelector";
import { formatMeasure, MeasureSelector } from "~/components/ui/MeasureSelector";
import { useMarketData } from "~/hooks/market-data/useMarketData";
import { useDimensions } from "~/hooks/useDimensions";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { useMoney } from "~/hooks/useMoney";
import { usePermissions } from "~/hooks/usePermissions";
import { useSession } from "~/hooks/useSession";
import { useI18n } from "~/lib/i18n/useI18n";
import { compact, groupBy, orderBy, pick } from "~/lib/lodash";
import { roundTo } from "~/lib/math";
import { useFetchAllowedCompanyBenchmarkLevelsQuery } from "~/pages/api/benchmark-level/fetch-allowed-company-benchmark-levels";
import { type GetMarketDataStatsMeasure } from "~/pages/api/get-market-data-stats";
import { computeEmployeeCompensation, formatEmployeeName, type Measure } from "~/services/employee";
import { type EmployeeStatsMeasure } from "~/services/employee-stats/utils";
import { EmployeeLevel, formatBenchmarkLevel, formatLevel } from "~/services/employee/employeeLevel";
import {
  type MarketDataStats,
  type MarketDataStatsFromDataset,
  MarketDataStatsSource,
} from "~/services/marketCompensationStats";
import { type PercentileBounds } from "~/services/percentiles";

type Props = {
  useTestData: boolean;
  useBenchmarkLevelsInShare: boolean;
  disableInteractions?: boolean;
  currency: Currency;
  categories: EmployeeLevel[];
  benchmarkLevelIds: number[];
  input: MarketDataInput;
  onMeasureChange?: (value: GetMarketDataStatsMeasure) => void;
  marketDataMeasure: Measure;
  market: {
    locations?: LocationForSelector[];
    byLevel: {
      [key in string]?: MarketDataStats | null;
    };
  };
  company?: {
    byLevel: {
      [key in string]?: MarketDataStats | null;
    };
  };
  comparison?: {
    locations?: LocationForSelector[];
    byLevel: {
      [key in string]?: MarketDataStats | null;
    };
  };

  percentiles: PercentileBounds;
  isLoading: boolean;
};

export const MarketDataChart: React.FC<Props> = (props) => {
  const {
    useTestData,
    currency,
    categories,
    benchmarkLevelIds,
    useBenchmarkLevelsInShare,
    market,
    company,
    comparison,
    disableInteractions = false,
    input,
    onMeasureChange,
    marketDataMeasure,
    isLoading,
  } = props;
  const { t, formatPercentile, locale } = useI18n();
  const { CAN_ACCESS_LEVEL_FRAMEWORKS } = useFeatureFlags();

  const canAccessBenchmarkLevels = CAN_ACCESS_LEVEL_FRAMEWORKS || useBenchmarkLevelsInShare;
  const orderedCategories = canAccessBenchmarkLevels
    ? benchmarkLevelIds.sort((a, b) => a - b)
    : orderBy(categories, (level) => {
        return Object.keys(EmployeeLevel).indexOf(level);
      });

  const series = compact([
    ...Object.values(pick(market.byLevel, orderedCategories)),
    ...(company ? Object.values(pick(company.byLevel, orderedCategories)) : []),
    ...(comparison ? Object.values(pick(comparison.byLevel, orderedCategories)) : []),
  ]);

  return (
    <Stack className={classNames({ "relative rounded-md border-0 bg-slate-500": isLoading })}>
      <Card
        variant="outlined"
        className={classNames({ "overflow-x-scroll px-0 pb-10 pt-0": true, "opacity-75": isLoading })}
      >
        {useTestData && (
          <Stack direction="row" spacing={2} alignItems="center" className="border-b bg-secondary-500 py-1 pl-2">
            <Ping color="white" />
            <Typography variant="subtitle2" color="common.white">
              {t("pages.market-data.market-data-chart.this-is-dummy-compensation-data")}
            </Typography>
          </Stack>
        )}

        <CircularProgress
          className={classNames({ "absolute left-1/2 top-1/3 z-99 ": isLoading, "hidden": !isLoading })}
        />
        <div className="min-w-[904px]">
          <Grid container alignItems="center" className="border-b border-gray-200 bg-gray-50">
            <Grid item md={1.5} className="py-3 !pl-6">
              <Typography variant="subtitle2" color="gray.600">
                {t("pages.market-data.market-data-chart.level")}
              </Typography>
            </Grid>

            <Grid item md={1.5} className={classNames("py-3", { "pl-6": locale === UserLocale.EN })}>
              <Typography variant="subtitle2" color="gray.600">
                {t("pages.market-data.market-data-chart.data-quality")}
              </Typography>
            </Grid>

            <Grid item md={2} className="py-3 pr-6" textAlign="right">
              <Typography variant="subtitle2" color="gray.600">
                {t("pages.market-data.market-data-chart.your-target", {
                  percentile: formatPercentile(input.percentiles.middle),
                })}
              </Typography>
            </Grid>

            <Grid item md={7} className="grid justify-center py-3">
              <MeasureSelector
                showDetailedMeasures
                disabled={disableInteractions}
                inputValue={marketDataMeasure}
                onChange={(value) => onMeasureChange?.(value as EmployeeStatsMeasure)}
              />
            </Grid>
          </Grid>

          {orderedCategories.map((level, index) => {
            const marketStats = market.byLevel[level];
            const companyStats = company?.byLevel[level] ?? null;

            return (
              <MarketDataChartRow
                key={level}
                currency={currency}
                level={level}
                marketDataMeasure={marketDataMeasure}
                market={marketStats ?? null}
                company={companyStats ?? undefined}
                useTestData={useTestData}
                series={series}
                disabled={disableInteractions}
                input={input}
                comparisonStats={comparison?.byLevel[level] ?? undefined}
                showFigures={index === orderedCategories.length - 1}
                isLoading={isLoading}
              />
            );
          })}
        </div>
      </Card>
    </Stack>
  );
};

type MarketDataChartRowProps = {
  level: number | EmployeeLevel;
  market: MarketDataStats | null;
  currency: Currency;
  marketDataMeasure: Measure;
  series: (MarketDataStats | null)[];
  disabled: boolean;
  company?: MarketDataStats;
  input: MarketDataInput;
  useTestData: boolean;
  comparisonStats?: MarketDataStats;
  showFigures: boolean;
  isLoading: boolean;
};

const MarketDataChartRow: React.FC<MarketDataChartRowProps> = ({
  market,
  level,
  currency,
  marketDataMeasure,
  series,
  disabled,
  company,
  useTestData,
  input,
  comparisonStats,
  showFigures,
  isLoading,
}) => {
  const { t } = useI18n();
  const { user } = useSession();
  const chartRef = useRef<HTMLDivElement>(null);
  const chartWidth = useDimensions(chartRef)?.width as number;
  const money = useMoney(currency);
  const { getMarketValue, getMarketBoundaries } = useMarketData({ marketDataMeasure, currency });

  const { min, max } = getMarketBoundaries(series);
  const { CAN_ACCESS_LEVEL_FRAMEWORKS } = useFeatureFlags();

  const canAccessBenchmarkLevels = CAN_ACCESS_LEVEL_FRAMEWORKS || input.useBenchmarkLevelsInShare;

  const { data: allowedBenchmarkLevels } = useFetchAllowedCompanyBenchmarkLevelsQuery();

  return (
    <Grid
      container
      key={level}
      className={classNames("border-b border-gray-200", { "h-[72px]": !comparisonStats, "h-32": comparisonStats })}
    >
      <Grid item md={1.5} className="py-6 pl-6">
        <Typography textTransform="capitalize" variant="subtitle2">
          {!canAccessBenchmarkLevels
            ? formatLevel(t, level as EmployeeLevel, user?.company.useAdvancedLevels ?? true)
            : allowedBenchmarkLevels && `${formatBenchmarkLevel(level as number, allowedBenchmarkLevels)}`}
        </Typography>
      </Grid>

      <Grid item md={1.5} className="py-6 pl-6">
        <Stack spacing={6}>
          {market && market.dataQuality ? (
            <DataQualityIndicator
              dataQuality={market.dataQuality}
              dataQualityDetailledByJob={getMarketValue(market).dataQualityDetailledByJob}
              {...(market.source === MarketDataStatsSource.DATASET && {
                employeeCount: market.count,
                companyCount: market.distinctCompaniesCount,
              })}
            />
          ) : (
            <Typography>-</Typography>
          )}

          {comparisonStats && comparisonStats.dataQuality && (
            <DataQualityIndicator
              dataQuality={comparisonStats.dataQuality}
              {...(comparisonStats.source === MarketDataStatsSource.DATASET && {
                employeeCount: comparisonStats.count,
                companyCount: comparisonStats.distinctCompaniesCount,
              })}
            />
          )}
        </Stack>
      </Grid>

      <Grid item md={2} className="px-6 py-6">
        <Stack spacing={6} textAlign="right">
          {market ? (
            <Typography>{money.format(getMarketValue(market).middle.value, { roundTo: 100 })}</Typography>
          ) : (
            <Typography>-</Typography>
          )}

          {comparisonStats && (
            <Typography>{money.format(getMarketValue(comparisonStats).middle.value, { roundTo: 100 })}</Typography>
          )}
        </Stack>
      </Grid>

      <Grid item md={7} className="relative flex h-full" ref={chartRef}>
        {market ? (
          <MarketDataBoxPlot
            top={22}
            level={level}
            market={market}
            currency={currency}
            marketDataMeasure={marketDataMeasure}
            series={series}
            disabled={disabled}
            company={company ?? null}
            input={input.market}
            percentiles={input.percentiles}
            useTestData={useTestData}
          />
        ) : (
          <>
            <Stack
              direction="row"
              className="absolute left-[40%] top-[20px] z-30 m-auto rounded bg-gray-100 px-2 py-1"
              spacing={1}
            >
              {!isLoading && <DoNotDisturbAlt fontSize="small" className="z-30 text-gray-300" />}

              <Typography variant="caption" color="gray.600" zIndex={30}>
                {isLoading ? t("common.loading") : t("pages.market-data.market-data-chart.sample-too-small")}
              </Typography>
            </Stack>
          </>
        )}

        {comparisonStats && (
          <MarketDataBoxPlot
            top={66}
            level={level}
            market={comparisonStats}
            currency={currency}
            marketDataMeasure={marketDataMeasure}
            series={series}
            disabled={disabled}
            company={company ?? null}
            input={input.compare}
            percentiles={input.percentiles}
            useTestData={useTestData}
            color="blue"
          />
        )}

        {chartWidth && (
          <MarketDataChartBackground
            showFigures={showFigures}
            currency={currency}
            min={min}
            max={max}
            chartWidth={chartWidth}
          />
        )}
      </Grid>
    </Grid>
  );
};

type MarketDataBoxPlotProps = {
  level: number | EmployeeLevel;
  market: MarketDataStats;
  currency: Currency;
  marketDataMeasure: Measure;
  series: (MarketDataStats | null)[];
  disabled: boolean;
  company: MarketDataStats | null;
  input: ComponentProps<typeof MarketDataChartTooltip>["input"];
  percentiles: PercentileBounds;
  useTestData: boolean;
  top: number;
  color?: ComponentProps<typeof MarketDataChartTooltip>["color"];
};

const MarketDataBoxPlot: React.FC<MarketDataBoxPlotProps> = ({
  market,
  currency,
  disabled,
  input,
  level,
  marketDataMeasure,
  useTestData,
  series,
  top,
  percentiles,
  color = "primary",
}) => {
  const { getMarketValue, getMarketBoundaries } = useMarketData({ marketDataMeasure, currency });
  const { permissions } = usePermissions();

  const { min, amplitude } = getMarketBoundaries(series);

  const amountToPosition = (amount: number) => (amount - min) / amplitude;

  const minLeft = amountToPosition(getMarketValue(market).lowest.value);
  const left = amountToPosition(getMarketValue(market).low.value);
  const right = amountToPosition(getMarketValue(market).high.value);
  const maxRight = amountToPosition(getMarketValue(market).highest.value);
  const midpoint = amountToPosition(getMarketValue(market).middle.value);
  const width = right - left;

  const dataPointsLines = groupBy(market.companyEmployees, (employee) => {
    return roundTo(
      computeEmployeeCompensation(employee, {
        measure: marketDataMeasure,
        targetCurrency: currency,
      }),
      1000
    );
  });

  return (
    <>
      {/* Left part of the mustache */}
      <div
        className={classNames("absolute  z-10 h-px -translate-y-1/2 transition-all", {
          "bg-primary-400": color === "primary",
          "bg-secondary-400": color === "secondary",
          "bg-blue-400": color === "blue",
        })}
        style={{
          left: `${minLeft * 100}%`,
          width: `calc(${(left - minLeft) * 100}% + 8px)`,
          top: `${top + 12}px`,
          willChange: "left, width",
        }}
      >
        <div
          className={classNames("absolute left-0 h-3 w-px -translate-y-1/2", {
            "bg-primary-400": color === "primary",
            "bg-secondary-400": color === "secondary",
            "bg-blue-400": color === "blue",
          })}
        />
      </div>

      <div
        className={classNames("pointer-events-none absolute z-30 h-6", {
          "bg-primary-400": color === "primary",
          "bg-secondary-400": color === "secondary",
          "bg-blue-400": color === "blue",
        })}
        style={{ left: `${midpoint * 100}%`, width: `1px`, top: `${top}px` }}
      />

      <div
        className="absolute inset-y-3 z-20 h-6 transition-all"
        style={{ left: `${left * 100}%`, width: `${width * 100}%`, top: `${top}px`, willChange: "left, width" }}
      >
        <MarketDataChartTooltip
          useBenchmarkLevelsInShare
          color={color}
          currency={currency}
          disabled={disabled}
          input={input}
          level={level}
          measure={marketDataMeasure}
          percentiles={percentiles}
          stats={market}
          useTestData={useTestData}
          placement="left"
        >
          <div
            className={classNames("absolute inset-0 rounded-md border shadow-sm", {
              "border-primary-400 bg-primary-300": color === "primary",
              "border-secondary-400 bg-secondary-300": color === "secondary",
              "border-blue-400 bg-blue-300": color === "blue",
            })}
          />
        </MarketDataChartTooltip>
      </div>

      {/* Right part of the mustache */}
      <div
        className={classNames("absolute z-10 h-px -translate-y-1/2 transition-all", {
          "bg-primary-400": color === "primary",
          "bg-secondary-400": color === "secondary",
          "bg-blue-400": color === "blue",
        })}
        style={{
          left: `calc(${right * 100}% - 8px)`,
          width: `${(maxRight - right) * 100}%`,
          top: `${top + 12}px`,
          willChange: "left, width",
        }}
      >
        <div
          className={classNames("absolute right-0 h-3 w-px -translate-y-1/2", {
            "bg-primary-400": color === "primary",
            "bg-secondary-400": color === "secondary",
            "bg-blue-400": color === "blue",
          })}
        />
      </div>

      {permissions.canAccessYouVsMarket &&
        Object.entries(dataPointsLines).map(([value, dataPoints]) => {
          const firstDataPoint = dataPoints[0];

          if (!firstDataPoint) return;

          const start = amountToPosition(parseInt(value, 10));

          if (dataPoints.length > 1) {
            return (
              <HtmlTooltip
                key={firstDataPoint.id}
                placement="bottom"
                title={
                  <MarketDataEmployeeTooltip employees={dataPoints} currency={currency} measure={marketDataMeasure} />
                }
              >
                <div
                  key={firstDataPoint.id}
                  style={{ left: `${start * 100}%`, top: `${top + 28}px` }}
                  className="absolute z-20 flex h-[18px] w-[18px] -translate-x-1/2 items-center justify-center rounded-full border bg-secondary-400 text-xs font-bold text-white drop-shadow"
                >
                  {dataPoints.length}
                </div>
              </HtmlTooltip>
            );
          }

          return (
            <div
              key={firstDataPoint.id}
              className="absolute z-20 -translate-x-1/2"
              style={{ left: `${start * 100}%`, top: `${top + 28}px` }}
            >
              <HtmlTooltip
                key={firstDataPoint.id}
                placement="bottom"
                title={
                  <MarketDataEmployeeTooltip employees={dataPoints} currency={currency} measure={marketDataMeasure} />
                }
              >
                <div>
                  <EmployeePicture size="sm" picture={null} placeholderId={firstDataPoint.id} />
                </div>
              </HtmlTooltip>
            </div>
          );
        })}
    </>
  );
};

type MarketDataEmployeeTooltipProps = {
  employees: MarketDataStatsFromDataset["companyEmployees"];
  currency: Currency;
  measure: Measure;
};

const MarketDataEmployeeTooltip: React.FC<MarketDataEmployeeTooltipProps> = ({ employees, currency, measure }) => {
  const money = useMoney(currency);
  const { t } = useI18n();
  const { user } = useSession();

  return (
    <Stack className="max-w-md">
      <Stack direction="row" justifyContent="space-between" className="border-b bg-gray-50 px-2 py-1">
        <Typography variant="caption" color="text.secondary">
          {t("pages.market-data.market-data-chart.employee", { count: employees?.length ?? 0 })}
        </Typography>

        <Typography variant="caption" color="text.secondary">
          {formatMeasure(t, measure)}
        </Typography>
      </Stack>

      {employees?.map((employee) => (
        <Stack
          direction="row"
          key={employee.id}
          spacing={10}
          alignItems="center"
          className="w-full overflow-hidden border-b p-2"
        >
          <Stack direction="row" className="w-full overflow-hidden" spacing={2}>
            <EmployeePicture size="lg" className="my-auto min-w-[32px]" picture={null} placeholderId={employee.id} />

            <Stack className="overflow-hidden">
              <Link
                to={{ pathname: "/people/market-data", query: { query: `${employee.firstName} ${employee.lastName}` } }}
                className="underline decoration-dashed"
                newTab
              >
                <Typography variant="subtitle2">
                  {formatEmployeeName(employee)}
                  <OpenInNew fontSize="inherit" className="mb-1 ml-1" />
                </Typography>
              </Link>

              <Typography variant="caption" color="text.secondary" noWrap>
                {formatLevel(t, employee.level, user?.company.useAdvancedLevels ?? true)} {employee.job.name} -{" "}
                {employee.location.name}, {employee.location.country.name}
              </Typography>
            </Stack>
          </Stack>

          <Typography variant="subtitle2">
            {money.format(computeEmployeeCompensation(employee, { targetCurrency: currency, measure }), {
              roundTo: 100,
            })}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};
